import React, { useState } from 'react';
import { Mail, Phone, Globe, MapPin, Pencil, Images, Play, Trash2, ChevronDown, ChevronUp, ExternalLink, Briefcase, Building } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';
import { BrandIcons, getSocialLinks, getExtraLinks, extractYouTubeVideoId } from './BrandIcons';
import { GalleryGrid } from './GalleryLightbox';

const BioTemplateGradient = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const socialLinks = getSocialLinks(card);
  const extraLinks = getExtraLinks(card);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 py-4 px-4">
      <div className="max-w-md mx-auto">
        {/* Card Container */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20">
          {/* Profile - Large Image */}
          <div className="text-center pt-6 pb-4 px-5">
            <div className="relative inline-block mb-4">
              {card.profileImage ? (
                <img src={card.profileImage} alt={card.name} className="w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-white/30 shadow-2xl object-cover" />
              ) : (
                <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-white/30 shadow-2xl bg-white/10 flex items-center justify-center">
                  <span className="text-5xl font-bold text-white">{card.name?.charAt(0) || 'U'}</span>
                </div>
              )}
              {isOwner && onUpdateProfileImage && (
                <label htmlFor="profile-upload-gradient" className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-purple-50">
                  <Pencil className="w-4 h-4 text-purple-600" />
                  <input id="profile-upload-gradient" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
                </label>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{card.name}</h1>
            {card.title && <p className="text-purple-200 text-sm flex items-center justify-center gap-1"><Briefcase className="w-3.5 h-3.5" />{card.title}</p>}
            {card.company && <p className="text-purple-300/70 text-xs flex items-center justify-center gap-1 mt-0.5"><Building className="w-3 h-3" />{card.company}</p>}
          </div>

          {/* Content */}
          <div className="px-5 pb-6">
            {/* Contact */}
            <div className="space-y-2 mb-5">
              {card.phone && (
                <a href={`tel:${card.phone}`} onClick={() => trackLinkClick('phone', card.phone)} className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors">
                  <Phone className="w-4 h-4 text-purple-300" /><span className="text-white text-sm">{card.phone}</span>
                </a>
              )}
              {card.email && (
                <a href={`mailto:${card.email}`} onClick={() => trackLinkClick('email', card.email)} className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors">
                  <Mail className="w-4 h-4 text-purple-300" /><span className="text-white text-sm break-all">{card.email}</span>
                </a>
              )}
              {(card.website || (card.websiteName && card.websiteTLD)) && (
                <a href={card.website || `https://${card.websiteName}${card.websiteTLD}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors">
                  <Globe className="w-4 h-4 text-purple-300" /><span className="text-white text-sm break-all">{(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              {(card.location || card.locationAddress) && (
                <a href={
                  card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                    ? card.location 
                    : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
                } target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors">
                  <MapPin className="w-4 h-4 text-purple-300" /><span className="text-white text-sm">{card.locationAddress || card.location || 'View Location'}</span>
                </a>
              )}
            </div>

            {/* Social */}
            {socialLinks.length > 0 && (
              <div className="flex justify-center flex-wrap gap-3 mb-5">
                {socialLinks.map((social, index) => {
                  const BrandIcon = BrandIcons[social.type];
                  return (
                    <a key={index} href={constructSocialUrl(social.type, social.value)} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick(social.type, social.value)} className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-lg hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110" title={social.label}>
                      {BrandIcon && <BrandIcon size={20} />}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Extra Links */}
            {extraLinks.length > 0 && (
              <div className="space-y-2 mb-5">
                {extraLinks.map((link, index) => (
                  <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('extraLink', link.url)} className="flex items-center gap-3 p-3 bg-white/20 backdrop-blur-lg rounded-xl hover:bg-white/30 transition-colors">
                    <ExternalLink className="w-4 h-4 text-white" /><span className="text-white text-sm font-medium">{link.title}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Bio */}
            {card.richBio && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 mb-5">
                <div className={`overflow-hidden transition-all duration-300 text-sm text-purple-100 ${!isBioExpanded ? 'max-h-12' : 'max-h-[2000px]'}`}>
                  <BioRichTextDisplay content={card.richBio} />
                </div>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="flex items-center gap-1 mt-2 text-xs text-purple-300 hover:text-white">
                  {isBioExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Show less</> : <><ChevronDown className="w-3.5 h-3.5" />Read more</>}
                </button>
              </div>
            )}

            {/* Gallery */}
            {hasGallery && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3"><Images className="w-4 h-4 text-purple-300" /><h3 className="text-sm font-medium text-white">Gallery</h3></div>
                <GalleryGrid 
                  images={card.galleryImages} 
                  onDeleteImage={card.onDeleteImage} 
                  isOwner={isOwner}
                  accentColor="#a855f7"
                />
              </div>
            )}

            {/* Videos */}
            {hasVideos && (
              <div>
                <div className="flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-purple-300" /><h3 className="text-sm font-medium text-white">Videos</h3></div>
                <div className="space-y-2">
                  {card.youtubeVideos.slice(0, 3).map((videoId, index) => {
                    const cleanVideoId = extractYouTubeVideoId(videoId);
                    return (
                      <div key={index} className="relative aspect-video rounded-xl overflow-hidden group">
                        <iframe src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        {isOwner && card.onDeleteVideo && <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioTemplateGradient;
